import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { NavigationContainer, NavigationState, PartialState } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { Platform, Alert } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/auth-context';
import { useOrganizationContext } from '../context/organization-context';
import ErrorBoundary from '../components/common/error-boundary';
import { TabNavigator } from '../navigation/TabNavigator';
import { LoadingScreen } from '../navigation/components/LoadingScreen';
import { linking } from '../navigation/config/linking';
import { RootStackParamList } from '../navigation/types/navigation.types';
import { SCREEN_NAMES, SCREEN_TITLES } from '../navigation/config/constants';
import { createLazyScreen } from '../navigation/utils/lazyScreen';
import rateLimitingService from '../services/rate-limiting-service';
import { logEvent } from '../utils/analytics';
import { log } from '../services/production-logger';


// Lazy load screens
const AuthNavigator = createLazyScreen(() =>
  import('./auth-navigator').then(m => ({ default: m.default })),
);
const OrganizationOnboardingScreen = createLazyScreen(
  () => import('../screens/onboarding/organization-onboarding-screen'),
);
const AcceptInvitationScreen = createLazyScreen(
  () => import('../screens/auth/accept-invitation-screen'),
);
const ProfileStack = createLazyScreen(() =>
  import('../navigation/stacks/ProfileStack').then(m => ({ default: m.ProfileStack })),
);

const MainStack = createStackNavigator<RootStackParamList>();

// Navigation state persistence key
const NAVIGATION_PERSISTENCE_KEY = '@InvoiceAI:navigationState';
const NAVIGATION_STATE_TIMEOUT = 5000; // 5 seconds timeout for state restoration

// Deep link validation
interface DeepLinkValidationResult {
  isValid: boolean;
  isSecure: boolean;
  requiredAuth: boolean;
  error?: string;
}

function validateDeepLink(url: string): DeepLinkValidationResult {
  try {
    // Parse URL
    const urlObj = new URL(url);
    
    // Check if URL is from allowed domains
    const allowedDomains = [
      'app.invoiceai.com',
      'invoiceai.app',
      'localhost',
    ];
    
    // For custom scheme, allow invoiceai://
    const isCustomScheme = url.startsWith('invoiceai://');
    const isDomainAllowed = allowedDomains.some(domain => urlObj.hostname === domain || urlObj.hostname.includes(domain));
    const isSecure = urlObj.protocol === 'https:' || urlObj.protocol === 'invoiceai:' || urlObj.hostname === 'localhost';
    
    if (!isCustomScheme && !isDomainAllowed) {
      return {
        isValid: false,
        isSecure: false,
        requiredAuth: false,
        error: 'Domain not allowed',
      };
    }
    
    // Check for secure connections (except localhost)
    if (!isSecure && !isCustomScheme && urlObj.hostname !== 'localhost') {
      return {
        isValid: false,
        isSecure: false,
        requiredAuth: false,
        error: 'Insecure connection not allowed',
      };
    }
    
    // Determine if authentication is required based on path
    const publicPaths = ['/login', '/register', '/forgot-password', '/accept-invitation'];
    const requiresAuth = !publicPaths.some(path => urlObj.pathname.startsWith(path));
    
    return {
      isValid: true,
      isSecure,
      requiredAuth: requiresAuth,
    };
  } catch (error) {
    return {
      isValid: false,
      isSecure: false,
      requiredAuth: false,
      error: `Invalid URL format: ${error.message}`,
    };
  }
}

function AppNavigator() {
  const { isAuthenticated, isAuthReady } = useAuth();
  const {
    userMemberships,
    isLoading: isMembershipsLoading,
    organization: currentOrganization,
  } = useOrganizationContext();
  const { t } = useTranslation();
  
  // Navigation state persistence
  const [initialState, setInitialState] = useState<NavigationState | PartialState<NavigationState> | undefined>();
  const [isReady, setIsReady] = useState(false);
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);

  // Memoize access checks
  const hasOrganizationAccess = useMemo(
    () => currentOrganization !== null || (userMemberships && userMemberships.length > 0),
    [currentOrganization, userMemberships],
  );

  const isCheckingAccess = useMemo(
    () => !isAuthReady || (isAuthenticated && isMembershipsLoading) || !isReady,
    [isAuthReady, isAuthenticated, isMembershipsLoading, isReady],
  );
  
  // Navigation state restoration
  useEffect(() => {
    const restoreState = async () => {
      try {
        const savedStateString = await AsyncStorage.getItem(NAVIGATION_PERSISTENCE_KEY);
        
        if (savedStateString) {
          const savedState = JSON.parse(savedStateString);
          
          // Validate that the saved state is not too old (24 hours)
          const stateAge = Date.now() - (savedState.timestamp || 0);
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours
          
          if (stateAge < maxAge) {
            setInitialState(savedState.state);
            logEvent('navigation_state_restored', { stateAge });
          } else {
            // Clear old state
            await AsyncStorage.removeItem(NAVIGATION_PERSISTENCE_KEY);
            logEvent('navigation_state_expired', { stateAge });
          }
        }
      } catch (error) {
        log.error('Error restoring navigation state:', error);
        // Clear corrupted state
        await AsyncStorage.removeItem(NAVIGATION_PERSISTENCE_KEY);
      } finally {
        // Add timeout to prevent indefinite loading
        setTimeout(() => setIsReady(true), NAVIGATION_STATE_TIMEOUT);
      }
    };
    
    restoreState();
  }, []);
  
  // Handle navigation state changes for persistence
  const handleStateChange = useCallback(async (state: NavigationState | undefined) => {
    if (!state) return;
    
    try {
      const stateToSave = {
        state,
        timestamp: Date.now(),
      };
      await AsyncStorage.setItem(NAVIGATION_PERSISTENCE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      log.error('Error saving navigation state:', error);
    }
  }, []);
  
  // Enhanced deep link handling with validation
  const handleDeepLink = useCallback(async (url: string) => {
    try {
      // Rate limit deep link attempts to prevent abuse
      const rateLimitKey = 'deep_link_handling';
      const isAllowed = await rateLimitingService.isRequestAllowed(rateLimitKey, {
        maxRequests: 10, // Max 10 deep links per minute
        windowMs: 60 * 1000, // 1 minute window
      });
      
      if (!isAllowed) {
        setDeepLinkError('Too many deep link attempts. Please try again later.');
        return;
      }
      
      // Validate deep link
      const validation = validateDeepLink(url);
      
      if (!validation.isValid) {
        setDeepLinkError(validation.error || 'Invalid deep link');
        logEvent('deep_link_validation_failed', { 
          url: url.replace(/[?&]token=[^&]*/g, ''), // Remove sensitive tokens from logs
          error: validation.error,
        });
        
        // Show user-friendly error
        Alert.alert(
          'Invalid Link',
          'This link is not valid or secure. Please check the link and try again.',
          [{ text: 'OK', onPress: () => setDeepLinkError(null) }]
        );
        return;
      }
      
      // Check authentication requirements
      if (validation.requiredAuth && !isAuthenticated) {
        setDeepLinkError('Authentication required for this link');
        logEvent('deep_link_auth_required', { 
          url: url.replace(/[?&]token=[^&]*/g, ''),
        });
        
        Alert.alert(
          'Authentication Required',
          'Please log in to access this content.',
          [{ text: 'OK', onPress: () => setDeepLinkError(null) }]
        );
        return;
      }
      
      // Log successful deep link handling
      logEvent('deep_link_handled', {
        url: url.replace(/[?&]token=[^&]*/g, ''),
        isSecure: validation.isSecure,
        requiredAuth: validation.requiredAuth,
      });
      
      // Clear any previous errors
      setDeepLinkError(null);
      
    } catch (error) {
      log.error('Error handling deep link:', error);
      setDeepLinkError('Error processing link');
      
      logEvent('deep_link_error', {
        url: url.replace(/[?&]token=[^&]*/g, ''),
        error: error.message,
      });
    }
  }, [isAuthenticated]);
  
  // Enhanced linking configuration with validation
  const enhancedLinking = useMemo(() => ({
    ...linking,
    // Add custom getInitialURL to handle validation
    getInitialURL: async () => {
      if (Platform.OS === 'web') {
        return window.location.href;
      }
      
      try {
        // For React Native, you would typically use Linking.getInitialURL()
        // const url = await Linking.getInitialURL();
        // For now, we'll return null and let the default behavior handle it
        return null;
      } catch (error) {
        log.error('Error getting initial URL:', error);
        return null;
      }
    },
    // Add subscribe function to handle URL changes
    subscribe: (listener: (url: string) => void) => {
      if (Platform.OS === 'web') {
        const handlePopState = () => {
          const url = window.location.href;
          handleDeepLink(url);
          listener(url);
        };
        
        window.addEventListener('popstate', handlePopState);
        
        return () => window.removeEventListener('popstate', handlePopState);
      }
      
      // For React Native, you would typically use Linking.addEventListener
      // For now, return a no-op function
      return () => {};
    },
  }), [linking, handleDeepLink]);

  // Show loading state while checking authentication and organization status
  if (isCheckingAccess) {
    return (
      <NavigationContainer 
        linking={enhancedLinking}
        onStateChange={handleStateChange}
      >
        <MainStack.Navigator screenOptions={{ headerShown: false, cardStyle: { flex: 1 } }}>
          <MainStack.Screen name={SCREEN_NAMES.LOADING} component={LoadingScreen} />
        </MainStack.Navigator>
      </NavigationContainer>
    );
  }

  return (
    <ErrorBoundary
      level="critical"
      resetOnPropsChange
      resetKeys={['isAuthenticated', 'hasOrganizationAccess']}
      onError={(error, errorInfo) => {
        log.error('Critical navigation error:', error);
        log.error('Error info:', errorInfo);
        // In a production app, you might want to send this to crash reporting
        // crashlytics.recordError(error);
      }}
    >
      <NavigationContainer 
        linking={enhancedLinking}
        initialState={initialState}
        onStateChange={handleStateChange}
        onReady={() => {
          log.debug('Navigation container ready');
          logEvent('navigation_ready', {
            hasInitialState: !!initialState,
            isAuthenticated,
            hasOrganizationAccess,
          });
        }}
        fallback={<LoadingScreen />}
        onUnhandledAction={(action) => {
          log.warn('Unhandled navigation action:', action);
          logEvent('navigation_unhandled_action', {
            type: action.type,
            payload: action.payload,
          });
        }}
      >
        <ErrorBoundary 
          level="screen"
          onError={(error, errorInfo) => {
            log.error('Navigation screen error:', error);
            log.error('Error info:', errorInfo);
            
            // Log navigation errors
            logEvent('navigation_screen_error', {
              error: error.message,
              stack: error.stack,
              componentStack: errorInfo?.componentStack,
            });
            
            // Clear navigation state if corrupted
            if (error.message.includes('state') || error.message.includes('navigation')) {
              AsyncStorage.removeItem(NAVIGATION_PERSISTENCE_KEY).catch(clearError => {
                log.error('Error clearing navigation state:', clearError);
              });
            }
          }}
          resetKeys={[isAuthenticated, hasOrganizationAccess, deepLinkError]}
        >
          <MainStack.Navigator screenOptions={{ headerShown: false, cardStyle: { flex: 1 } }}>
            {!isAuthenticated ? (
              <>
                {/* Not authenticated - show auth screens */}
                <MainStack.Screen name={SCREEN_NAMES.AUTH_NAVIGATOR} component={AuthNavigator} />
                {/* AcceptInvitation can be accessed without auth */}
                <MainStack.Screen
                  name={SCREEN_NAMES.ACCEPT_INVITATION}
                  component={AcceptInvitationScreen}
                  options={{
                    headerShown: true,
                    title: t(SCREEN_TITLES.ACCEPT_INVITATION, 'Accept Invitation'),
                  }}
                />
              </>
            ) : !hasOrganizationAccess ? (
              <>
                {/* Authenticated but no organization - show onboarding */}
                <MainStack.Screen
                  name={SCREEN_NAMES.ORGANIZATION_ONBOARDING}
                  component={OrganizationOnboardingScreen}
                  options={{
                    headerShown: false,
                    // Prevent going back
                    gestureEnabled: false,
                    headerLeft: () => null,
                  }}
                />
                {/* AcceptInvitation for authenticated users without org */}
                <MainStack.Screen
                  name={SCREEN_NAMES.ACCEPT_INVITATION}
                  component={AcceptInvitationScreen}
                  options={{
                    headerShown: true,
                    title: t(SCREEN_TITLES.ACCEPT_INVITATION, 'Accept Invitation'),
                  }}
                />
              </>
            ) : (
              <>
                {/* Authenticated with organization - show main app */}
                <MainStack.Screen name={SCREEN_NAMES.TAB_NAVIGATOR} component={TabNavigator} />
                {/* AcceptInvitation for authenticated users with org (switching orgs) */}
                <MainStack.Screen
                  name={SCREEN_NAMES.ACCEPT_INVITATION}
                  component={AcceptInvitationScreen}
                  options={{
                    headerShown: true,
                    title: t(SCREEN_TITLES.ACCEPT_INVITATION, 'Accept Invitation'),
                  }}
                />
                {/* QR Scanner Screen */}
                <MainStack.Screen
                  name="QRScanner"
                  component={createLazyScreen(() => import('../screens/auth/qr-scanner-screen'))}
                  options={{
                    headerShown: false,
                    presentation: 'fullScreenModal',
                    gestureEnabled: false,
                  }}
                />
                {/* Profile Stack - accessible from anywhere */}
                <MainStack.Screen
                  name={SCREEN_NAMES.PROFILE_STACK}
                  component={ProfileStack}
                  options={{
                    headerShown: false,
                    presentation: 'modal',
                    animationTypeForReplace: 'push',
                  }}
                />
              </>
            )}
          </MainStack.Navigator>
        </ErrorBoundary>
      </NavigationContainer>
    </ErrorBoundary>
  );
}

export default AppNavigator;
